/*
  GREEN PAWLY – Warenkorb-Drawer

  Ein gemeinsamer Refresh über die Section Rendering API:
    GET <aktuelle Seite>?sections=helper-cart,gp-cart-upsells
  helper-cart liefert #AjaxCartForm und #AjaxCartSubtotal (wie der Theme-eigene
  Refresh in component-cart.js), gp-cart-upsells die Empfehlungen.

  Ersetzt die früheren Inline-Skripte in layout/theme.liquid, die per ?view=… die
  komplette Seite geladen und im Fehlerfall auf /cart umgeleitet haben.

  Der Theme-Refresh (component-cart.js / component-product-form.js) bleibt für
  "In den Warenkorb", Menge und Entfernen zuständig. Danach feuern dort die Events
  'add-to-cart' bzw. 'cart-updated'; hier werden dann nur die Empfehlungen nachgeladen.

  Aktionen
    - Empfehlungskarte "+ Ergänzen"   (form.js-drawer-add-to-cart)
    - Lückenfüller "+ Ergänzen"       ([data-gp-add-variant])
    - Größen-Upgrade                  ([data-gp-upgrade])
    - Abo-Umstellung                  ([data-gp-abo])
    - Geschenk ab Mindestbetrag       ([data-gp-gift], automatisch nach jedem Refresh)
  Bei Fehlern bleibt der Warenkorb, wie er war, und die Karte zeigt eine Meldung.
*/
(() => {
  if (window.gpCartDrawer) return;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const URL_ADD = `${ROOT}cart/add.js`;
  const URL_CHANGE = `${ROOT}cart/change.js`;
  const URL_CART = `${ROOT}cart.js`;

  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  async function cartRequest(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || `Warenkorb-Fehler ${response.status}`);
    }
    return data;
  }

  /* ---------- Rendern ---------- */

  function applyCart(html) {
    const doc = parse(html);
    const form = document.getElementById('AjaxCartForm');
    const subtotal = document.getElementById('AjaxCartSubtotal');
    const freshForm = doc.getElementById('AjaxCartForm');
    const freshSubtotal = doc.getElementById('AjaxCartSubtotal');

    if (form && freshForm) {
      form.innerHTML = freshForm.innerHTML;
      if (typeof form.ajaxifyCartItems === 'function') form.ajaxifyCartItems();
    }
    if (subtotal && freshSubtotal) subtotal.innerHTML = freshSubtotal.innerHTML;

    const count = form && form.querySelector('[data-cart-count]');
    const total = form && form.querySelector('[data-cart-total]');
    if (count) {
      document.querySelectorAll('[data-header-cart-count]').forEach((el) => { el.textContent = count.textContent; });
      const sidebar = document.getElementById('site-cart-sidebar');
      if (sidebar) {
        const items = parseInt(count.textContent, 10) || 0;
        sidebar.setAttribute('data-cart-items', items);
        sidebar.classList.toggle('cart-is-empty', items === 0);
      }
    }
    if (total) {
      document.querySelectorAll('[data-header-cart-total]').forEach((el) => { el.textContent = total.textContent; });
    }
  }

  function upsellsSlot() {
    return document.getElementById('shopify-section-gp-cart-upsells');
  }

  async function loadFallback(slot) {
    const placeholder = slot.querySelector('[data-gp-upsells-fallback-url]');
    if (!placeholder) return;
    try {
      const response = await fetch(placeholder.dataset.gpUpsellsFallbackUrl, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(response.status);
      const fresh = parse(await response.text()).getElementById('gp-cart-upsells');
      const current = slot.querySelector('#gp-cart-upsells');
      if (fresh && current) current.replaceWith(fresh);
    } catch (e) {
      placeholder.remove();
    }
  }

  async function applyUpsells(html) {
    const slot = upsellsSlot();
    if (!slot) return;
    const fresh = parse(html).getElementById('gp-cart-upsells');
    const current = slot.querySelector('#gp-cart-upsells');
    if (fresh && current) current.replaceWith(fresh);
    await loadFallback(slot);
  }

  async function refresh(sections = ['helper-cart', 'gp-cart-upsells']) {
    const response = await fetch(`${window.location.pathname}?sections=${sections.join(',')}`, {
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error(`Section Rendering ${response.status}`);
    const data = await response.json();
    if (data['helper-cart']) applyCart(data['helper-cart']);
    if (data['gp-cart-upsells']) await applyUpsells(data['gp-cart-upsells']);
    document.dispatchEvent(new CustomEvent('gp:cart-drawer-refreshed'));
    await syncGift();
  }

  // Geschenk hinzufügen bzw. entfernen, wie es snippets/gp-free-shipping.liquid vorgibt.
  // Nur Zeilen mit der Eigenschaft _gp_gift werden entfernt. Die Sperre verhindert Schleifen,
  // falls Shopify einen Aufruf ablehnt (z. B. Geschenk inzwischen ausverkauft).
  let giftBusy = false;
  let giftFailed = false;
  async function syncGift() {
    const box = document.querySelector('[data-gp-gift]');
    if (!box || giftBusy || giftFailed) return;
    const state = box.dataset.giftState;
    if (state !== 'add' && state !== 'remove') return;
    giftBusy = true;
    try {
      // Immer gegen den echten Warenkorb prüfen: Die Anzeige kann veraltet sein (z. B. auf der
      // Warenkorbseite, solange sie neu gerendert wird) – sonst landet das Geschenk doppelt im Korb.
      const cart = await (await fetch(URL_CART, { credentials: 'same-origin' })).json();
      const giftLines = cart.items.filter((item) => item.properties && item.properties._gp_gift);
      if (state === 'add') {
        if (giftLines.length > 0) { giftBusy = false; return; }
        await cartRequest(URL_ADD, { items: [{ id: Number(box.dataset.giftVariant), quantity: 1, properties: { _gp_gift: '1' } }] });
      } else {
        if (giftLines.length === 0) { giftBusy = false; return; }
        for (const line of giftLines) {
          await cartRequest(URL_CHANGE, { id: line.key, quantity: 0 });
        }
      }
    } catch (e) {
      console.error(e);
      giftFailed = true;
      giftBusy = false;
      return;
    }
    giftBusy = false;
    await refresh();
  }

  const refreshUpsells = () => refresh(['gp-cart-upsells']).catch((e) => console.error(e));

  /* ---------- Rückmeldung an der Karte ---------- */

  function setBusy(button, busy, text) {
    if (!button) return;
    if (busy) {
      button.dataset.gpLabel = button.textContent;
      button.textContent = text;
      button.disabled = true;
    } else {
      button.textContent = button.dataset.gpLabel || button.textContent;
      button.disabled = false;
    }
  }

  function showError(origin) {
    const box = origin.closest('.gp-drawer-recommendation, [data-gp-upgrade-box]');
    const error = box && box.querySelector('[data-gp-card-error]');
    if (error) error.hidden = false;
  }

  /* ---------- Aktionen ---------- */

  async function addVariant(origin, button, variantId) {
    setBusy(button, true, 'Wird hinzugefügt …');
    try {
      await cartRequest(URL_ADD, { items: [{ id: Number(variantId), quantity: 1 }] });
      await refresh();
    } catch (e) {
      console.error(e);
      setBusy(button, false);
      showError(origin);
    }
  }

  // Erst 14 kg hinzufügen, dann 5 kg reduzieren: Scheitert das Hinzufügen, ist noch
  // nichts verändert. Scheitert das Reduzieren, wird das Hinzufügen zurückgenommen.
  async function upgrade(button) {
    const lineKey = button.dataset.lineKey;
    const lineQty = parseInt(button.dataset.lineQty, 10) || 1;
    const toVariant = Number(button.dataset.toVariant);

    setBusy(button, true, 'Wird gewechselt …');

    try {
      await cartRequest(URL_ADD, { items: [{ id: toVariant, quantity: 1 }] });
    } catch (e) {
      console.error(e);
      setBusy(button, false);
      showError(button);
      return;
    }

    try {
      await cartRequest(URL_CHANGE, { id: lineKey, quantity: lineQty - 1 });
    } catch (e) {
      console.error(e);
      try {
        const cart = await (await fetch(URL_CART, { credentials: 'same-origin' })).json();
        const added = cart.items.find((item) => item.variant_id === toVariant && !item.selling_plan_allocation);
        if (added) await cartRequest(URL_CHANGE, { id: added.key, quantity: added.quantity - 1 });
      } catch (rollbackError) {
        console.error(rollbackError);
      }
      await refresh().catch(() => {});
      const again = document.querySelector(`[data-gp-upgrade][data-line-key="${CSS.escape(lineKey)}"]`);
      if (again) showError(again);
      return;
    }

    try {
      await refresh();
    } catch (e) {
      console.error(e);
      setBusy(button, false);
    }
  }

  // Dieselbe Zeile auf den Abo-Verkaufsplan umstellen (Appstle = native Selling Plans).
  // Ein einziger Aufruf: Scheitert er, bleibt der Warenkorb unverändert.
  async function switchToAbo(button) {
    setBusy(button, true, 'Wird umgestellt …');
    try {
      await cartRequest(URL_CHANGE, {
        id: button.dataset.lineKey,
        quantity: parseInt(button.dataset.lineQty, 10) || 1,
        selling_plan: Number(button.dataset.sellingPlan)
      });
      await refresh();
    } catch (e) {
      console.error(e);
      setBusy(button, false);
      showError(button);
    }
  }

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('.js-drawer-add-to-cart');
    if (!form) return;
    event.preventDefault();
    const id = new FormData(form).get('id');
    addVariant(form, form.querySelector('[type="submit"]'), id);
  });

  document.addEventListener('click', (event) => {
    const gap = event.target.closest('[data-gp-add-variant]');
    if (gap) {
      event.preventDefault();
      addVariant(gap, gap, gap.dataset.gpAddVariant);
      return;
    }
    const upgradeButton = event.target.closest('[data-gp-upgrade]');
    if (upgradeButton) {
      event.preventDefault();
      upgrade(upgradeButton);
      return;
    }
    const aboButton = event.target.closest('[data-gp-abo]');
    if (aboButton) {
      event.preventDefault();
      switchToAbo(aboButton);
    }
  });

  // Theme-Refresh ist durch: Empfehlungen nachziehen. Beide Events blubbern nicht,
  // deshalb in der Capture-Phase.
  document.addEventListener('add-to-cart', refreshUpsells, true);
  document.addEventListener('cart-updated', refreshUpsells, true);

  // Schnittstelle für Apps (component-cart.js definiert refreshCart ebenfalls);
  // ab hier inklusive Empfehlungen.
  window.refreshCart = () => refresh()
    .then(() => {
      const sidebar = document.querySelector('[data-js-site-cart-sidebar]');
      if (sidebar && typeof sidebar.show === 'function') sidebar.show();
    })
    .catch((e) => console.error(e));

  window.gpCartDrawer = { refresh, syncGift };

  // Erster Seitenaufruf: Fallback-Empfehlungen nachladen, falls die Liste leer ist.
  const slot = upsellsSlot();
  if (slot) loadFallback(slot);
  syncGift();
})();
